
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { MessageSquarePlus, ThumbsUp } from "lucide-react";
import { FeedbackDialog } from "./FeedbackDialog";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import type { Feedback } from "./types";

export function FeedbackSection() {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const { user } = useAuth();
  const { toast } = useToast();

  const { data: feedback = [], refetch } = useQuery({
    queryKey: ["feedback"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("feedback")
        .select("*, feedback_votes(user_id)")
        .order("created_at", { ascending: false });

      if (error) throw error;

      // Transform the data to include votes_count and has_voted
      return data.map((item: any) => ({
        ...item,
        votes_count: item.feedback_votes?.length || 0,
        has_voted: item.feedback_votes?.some((vote: any) => vote.user_id === user?.id) || false,
      })) as Feedback[];
    },
  });

  const handleVote = async (feedbackId: string, hasVoted: boolean) => {
    if (!user) {
      toast({
        title: "Je moet ingelogd zijn",
        description: "Log in om te kunnen stemmen op feedback.",
        variant: "destructive",
      });
      return;
    }

    try {
      if (hasVoted) {
        await supabase
          .from("feedback_votes")
          .delete()
          .match({ feedback_id: feedbackId, user_id: user.id });
      } else {
        await supabase
          .from("feedback_votes")
          .insert({ feedback_id: feedbackId, user_id: user.id });
      }
      
      refetch();
      
      toast({
        title: hasVoted ? "Stem verwijderd" : "Gestemd",
        description: hasVoted ? "Je stem is verwijderd" : "Je stem is toegevoegd",
      });
    } catch (error) {
      toast({
        title: "Er ging iets mis",
        description: "Je stem kon niet worden verwerkt. Probeer het later opnieuw.",
        variant: "destructive",
      });
    }
  };

  const featureRequests = feedback.filter((item) => item.type === "feature");

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-semibold text-gray-900">Feature Requests</h2>
        <Button onClick={() => setIsDialogOpen(true)} className="gap-2">
          <MessageSquarePlus className="h-4 w-4" /> Geef Feedback
        </Button>
      </div>

      <div className="space-y-4">
        {featureRequests.map((request) => (
          <div
            key={request.id}
            className="rounded-lg border bg-white p-4 shadow-sm"
          >
            <div className="flex items-start justify-between">
              <div>
                <h3 className="font-medium">
                  {request.title}{" "}
                  {request.status === "new" && (
                    <span className="ml-2 inline-flex items-center rounded-full bg-blue-50 px-2 py-1 text-xs font-medium text-blue-700">
                      Nieuw
                    </span>
                  )}
                </h3>
                <p className="mt-1 text-gray-600">{request.description}</p>
                {request.submitter_name && (
                  <p className="mt-2 text-sm text-gray-500">
                    Ingediend door: {request.submitter_name}
                  </p>
                )}
              </div>
              <Button
                variant="ghost"
                size="sm"
                className={`gap-2 ${request.has_voted ? "text-blue-600" : "text-gray-500"}`}
                onClick={() => handleVote(request.id, request.has_voted || false)}
              >
                <ThumbsUp className="h-4 w-4" />
                <span>{request.votes_count || 0}</span>
              </Button>
            </div>
          </div>
        ))}
      </div>

      <FeedbackDialog open={isDialogOpen} onOpenChange={setIsDialogOpen} />
    </div>
  );
}
